/**
 * MemberOverridePanel — look up a member's tier assignment and, optionally,
 * apply a manual override.
 *
 * GET /guardrails/members/:id/tier throws QuillApiError(404, NOT_FOUND) when
 * the member has no tier assignment yet — rendered as an EmptyState, not an
 * error. POST upserts an override and records the admin (from the
 * x-forwarded-admin-email header the proxy injects — not sent by the client).
 */

import React, { useState } from 'react';
import { Box, TextField, Typography, Button, MenuItem, Select, Alert, Paper, Divider, CircularProgress } from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EmptyState from '../../common/EmptyState';
import QuillActionConfirm from '../common/QuillActionConfirm';
import { colors } from '../../../theme/designTokens';
import {
  quillApi,
  QuillApiError,
  CapabilityTier,
  MemberTierAssignment,
} from '../../../services/quillAdminService';

interface MemberOverridePanelProps {
  tiers: CapabilityTier[];
}

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MemberOverridePanel: React.FC<MemberOverridePanelProps> = ({ tiers }) => {
  const [memberId, setMemberId] = useState('');
  const [lookupState, setLookupState] = useState<'idle' | 'loading' | 'found' | 'not_found' | 'error'>('idle');
  const [assignment, setAssignment] = useState<MemberTierAssignment | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [overrideTierId, setOverrideTierId] = useState('');
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applySuccess, setApplySuccess] = useState(false);

  const runLookup = async () => {
    const trimmed = memberId.trim();
    if (!trimmed) return;
    setLookupState('loading');
    setErrorMessage(null);
    setApplySuccess(false);
    setApplyError(null);
    try {
      const result = await quillApi.guardrails.getMemberTier(trimmed);
      setAssignment(result);
      setOverrideTierId(result.tier_id);
      setLookupState('found');
    } catch (e) {
      if (e instanceof QuillApiError && e.status === 404) {
        setAssignment(null);
        setOverrideTierId(tiers[0]?.tier_id ?? '');
        setLookupState('not_found');
      } else {
        setErrorMessage(e instanceof QuillApiError ? e.message : 'Failed to look up member tier');
        setLookupState('error');
      }
    }
  };

  const handleApplyClick = () => {
    if (!overrideTierId || !reason.trim()) return;
    setConfirmOpen(true);
  };

  const handleConfirmApply = async () => {
    const trimmed = memberId.trim();
    setApplying(true);
    setApplyError(null);
    try {
      await quillApi.guardrails.setMemberTier(trimmed, {
        tier_id: overrideTierId,
        reason: reason.trim(),
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      setApplySuccess(true);
      setConfirmOpen(false);
      await runLookup();
    } catch (e) {
      setApplyError(e instanceof QuillApiError ? e.message : 'Failed to apply override');
    } finally {
      setApplying(false);
    }
  };

  const selectedTier = tiers.find((t) => t.tier_id === overrideTierId);

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PersonSearchIcon sx={{ color: colors.deepTeal[500] }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Member Tier Override
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Member ID (UUID)"
          value={memberId}
          onChange={(e) => setMemberId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runLookup();
          }}
          sx={{ minWidth: 340 }}
        />
        <Button
          variant="contained"
          onClick={runLookup}
          disabled={!memberId.trim() || lookupState === 'loading'}
          sx={{ backgroundColor: colors.deepTeal[500], '&:hover': { backgroundColor: colors.deepTeal[600] } }}
        >
          {lookupState === 'loading' ? <CircularProgress size={18} color="inherit" /> : 'Look up'}
        </Button>
      </Box>

      {memberId.trim() && !UUID_LIKE.test(memberId.trim()) && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          Doesn't look like a UUID — lookup will still run against whatever you enter.
        </Typography>
      )}

      {lookupState === 'error' && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

      {lookupState === 'not_found' && (
        <EmptyState
          icon="no_results"
          title="No record for this member"
          description="This member has no tier assignment yet. You can set one below."
        />
      )}

      {lookupState === 'found' && assignment && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Currently on <strong>{assignment.tier_name}</strong> ({assignment.tier_id})
          {assignment.assigned_by_admin_id ? ` — assigned by ${assignment.assigned_by_admin_id}` : ''}
          {assignment.assignment_reason ? ` — "${assignment.assignment_reason}"` : ''}
          {assignment.expires_at ? ` — expires ${new Date(assignment.expires_at).toLocaleString()}` : ''}
        </Alert>
      )}

      {applySuccess && <Alert severity="success" sx={{ mb: 2 }}>Override applied.</Alert>}
      {applyError && <Alert severity="error" sx={{ mb: 2 }}>{applyError}</Alert>}

      {(lookupState === 'found' || lookupState === 'not_found') && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontWeight: 600, mb: 1.5 }}>Set / update override</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Select
              size="small"
              value={overrideTierId}
              onChange={(e) => setOverrideTierId(e.target.value)}
              displayEmpty
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="" disabled>
                Select tier
              </MenuItem>
              {tiers.map((t) => (
                <MenuItem key={t.tier_id} value={t.tier_id}>
                  {t.tier_name}
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              label="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <TextField
              size="small"
              label="Expires at (optional)"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              disabled={!overrideTierId || !reason.trim()}
              onClick={handleApplyClick}
              sx={{ backgroundColor: colors.warning.main, color: '#1B1F23', '&:hover': { backgroundColor: colors.warning.dark } }}
            >
              Apply override
            </Button>
          </Box>
        </>
      )}

      <QuillActionConfirm
        open={confirmOpen}
        title="Apply member tier override"
        message={`This immediately changes which guardrail tier member ${memberId.trim()} is evaluated under (production)${
          selectedTier ? ` — moving them to "${selectedTier.tier_name}".` : '.'
        }`}
        confirmLabel="Confirm & Save"
        loading={applying}
        pendingLabel="Applying…"
        onConfirm={handleConfirmApply}
        onCancel={() => setConfirmOpen(false)}
      />
    </Paper>
  );
};

export default MemberOverridePanel;
